import { StructuredJsonFieldDef } from '../../shared/design-system/structured-json-form.model';

export const GROWTH_CONNECTOR_DIRECTIONS = ['Inbound', 'Outbound', 'Bidirectional'] as const;
export const GROWTH_CONNECTOR_KINDS = ['External', 'Internal'] as const;
export const GROWTH_CONNECTOR_STATUSES = ['Draft', 'Active', 'Disabled'] as const;
export const GROWTH_CONNECTOR_SCHEDULE_MODES = ['OnDemand', 'Webhook', 'Stream'] as const;
export const GROWTH_CONNECTOR_SYNC_RUN_STATUSES = ['Running', 'Completed', 'Failed', 'IdempotentReplay'] as const;

/** Known EG-1..EG-3 lifecycle event types for internal-spine mapping. */
export const GROWTH_INTEGRATION_LIFECYCLE_SOURCES = [
  'party.updated',
  'catalog.offering.updated',
  'sales.opportunity.created',
  'sales.order.completed',
  'growth.context.created',
  'deployment.applied'
] as const;

export const GROWTH_INTEGRATION_LEAD_SOURCES = [
  'mock_erp_webhook',
  'form_submit',
  'request_demo',
  'request_quote',
  'social_comment',
  'social_dm',
  'manual'
] as const;

export const GROWTH_INTEGRATION_CHANNEL_KEYS = [
  'integration',
  'web',
  'email',
  'social',
  'landing',
  'sms'
] as const;

/** Built-in Growth auth profile keys (Tier-1 sandbox; not Notification secrets). */
export const GROWTH_INTEGRATION_BUILTIN_AUTH_CONFIG_REFS = [
  'growth:internal-spine',
  'growth:mock-erp'
] as const;

export const NOTIFICATION_PROVIDER_CONFIG_AUTH_PREFIX = 'notification:provider-config:';

export function toNotificationProviderAuthConfigRef(configId: string): string {
  return `${NOTIFICATION_PROVIDER_CONFIG_AUTH_PREFIX}${configId}`;
}

export function tryParseNotificationProviderConfigId(authConfigRef: string | null | undefined): string | null {
  if (!authConfigRef?.startsWith(NOTIFICATION_PROVIDER_CONFIG_AUTH_PREFIX)) {
    return null;
  }
  const id = authConfigRef.slice(NOTIFICATION_PROVIDER_CONFIG_AUTH_PREFIX.length).trim();
  return id || null;
}

const lifecycleSourceFieldKey = (eventType: string): string =>
  `source:${eventType}`;

export const growthConnectorMappingFields: StructuredJsonFieldDef[] = [
  {
    key: 'mode',
    type: 'select',
    labelKey: 'growth.integration.mapping.fields.mode',
    options: [
      { value: 'lifecycle-handler', labelKey: 'growth.integration.mapping.mode.lifecycleHandler' },
      { value: 'webhook-lead', labelKey: 'growth.integration.mapping.mode.webhookLead' },
      { value: 'outbound-stub', labelKey: 'growth.integration.mapping.mode.outboundStub' }
    ]
  },
  {
    key: 'leadSource',
    type: 'select',
    labelKey: 'growth.integration.mapping.fields.leadSource',
    options: GROWTH_INTEGRATION_LEAD_SOURCES.map(value => ({
      value,
      labelKey: `growth.integration.mapping.leadSource.${value}`
    }))
  },
  {
    key: 'channelKey',
    type: 'select',
    labelKey: 'growth.integration.mapping.fields.channelKey',
    options: GROWTH_INTEGRATION_CHANNEL_KEYS.map(value => ({
      value,
      labelKey: `growth.integration.mapping.channelKey.${value}`
    }))
  },
  { key: 'mapExternalPartyRef', type: 'boolean', labelKey: 'growth.integration.mapping.fields.mapExternalPartyRef' },
  ...GROWTH_INTEGRATION_LIFECYCLE_SOURCES.map(eventType => ({
    key: lifecycleSourceFieldKey(eventType),
    type: 'boolean' as const,
    labelKey: `growth.integration.mapping.lifecycleSource.${eventType}`
  }))
];

export const growthConnectorSyncSummaryFields: StructuredJsonFieldDef[] = [
  { key: 'result', type: 'string', labelKey: 'growth.integration.syncSummary.fields.result' },
  { key: 'mode', type: 'string', labelKey: 'growth.integration.syncSummary.fields.mode' },
  { key: 'eventType', type: 'string', labelKey: 'growth.integration.syncSummary.fields.eventType' },
  { key: 'externalPartyRef', type: 'string', labelKey: 'growth.integration.syncSummary.fields.externalPartyRef' },
  { key: 'consumedMarkers', type: 'number', labelKey: 'growth.integration.syncSummary.fields.consumedMarkers' },
  { key: 'sink', type: 'string', labelKey: 'growth.integration.syncSummary.fields.sink' },
  { key: 'providerKey', type: 'string', labelKey: 'growth.integration.syncSummary.fields.providerKey' },
  { key: 'note', type: 'textarea', labelKey: 'growth.integration.syncSummary.fields.note' }
];

export function emptyConnectorMappingJson(): string {
  const model: Record<string, unknown> = {
    mode: '',
    leadSource: '',
    channelKey: '',
    mapExternalPartyRef: false
  };
  for (const eventType of GROWTH_INTEGRATION_LIFECYCLE_SOURCES) {
    model[lifecycleSourceFieldKey(eventType)] = false;
  }
  return JSON.stringify(model);
}

export function normalizeConnectorMappingJson(payloadJson: string): string {
  try {
    const parsed = JSON.parse(payloadJson || '{}') as Record<string, unknown>;
    const selectedSources = new Set(parseLifecycleSources(parsed['sources']));
    const model: Record<string, unknown> = {
      mode: typeof parsed['mode'] === 'string' ? parsed['mode'] : '',
      leadSource: typeof parsed['leadSource'] === 'string' ? parsed['leadSource'] : '',
      channelKey: typeof parsed['channelKey'] === 'string' ? parsed['channelKey'] : '',
      mapExternalPartyRef: parsed['mapExternalPartyRef'] === true || parsed['mapExternalPartyRef'] === 'true'
    };
    for (const eventType of GROWTH_INTEGRATION_LIFECYCLE_SOURCES) {
      const fieldKey = lifecycleSourceFieldKey(eventType);
      model[fieldKey] = selectedSources.has(eventType) || parsed[fieldKey] === true || parsed[fieldKey] === 'true';
    }
    return JSON.stringify(model);
  } catch {
    return emptyConnectorMappingJson();
  }
}

export function mergeConnectorMappingJson(formPayloadJson: string): string {
  try {
    const parsed = JSON.parse(formPayloadJson || '{}') as Record<string, unknown>;
    const sources = GROWTH_INTEGRATION_LIFECYCLE_SOURCES.filter(eventType => {
      const value = parsed[lifecycleSourceFieldKey(eventType)];
      return value === true || value === 'true';
    });

    const result: Record<string, unknown> = {};
    if (typeof parsed['mode'] === 'string' && parsed['mode']) {
      result['mode'] = parsed['mode'];
    }
    if (typeof parsed['leadSource'] === 'string' && parsed['leadSource']) {
      result['leadSource'] = parsed['leadSource'];
    }
    if (typeof parsed['channelKey'] === 'string' && parsed['channelKey']) {
      result['channelKey'] = parsed['channelKey'];
    }
    if (parsed['mapExternalPartyRef'] === true || parsed['mapExternalPartyRef'] === 'true') {
      result['mapExternalPartyRef'] = true;
    }
    if (sources.length > 0) {
      result['sources'] = sources;
    }
    return JSON.stringify(result);
  } catch {
    return '{}';
  }
}

function parseLifecycleSources(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string' && !!item.trim()).map(item => item.trim());
  }
  if (typeof value === 'string' && value.trim()) {
    return value.split(',').map(item => item.trim()).filter(Boolean);
  }
  return [];
}
